import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const isCheckoutShippingPage = location.pathname.toLowerCase().startsWith("/checkoutshipping");
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isCheckoutShippingPage,
    };
};

interface OwnProps {
    firstName: string;
    lastName: string;
    attention: string;
    companyName: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    country: BaseAddressModel["country"];
    state: BaseAddressModel["state"];
    city: string;
    postalCode: string;
    phone: string;
    email: string;
    fax: string;
    isDefault: boolean;
    onChangeFirstName: TextFieldProps["onChange"];
    onChangeLastName: TextFieldProps["onChange"];
    onChangeAttention: TextFieldProps["onChange"];
    onChangeCompanyName: TextFieldProps["onChange"];
    onChangeAddress1: TextFieldProps["onChange"];
    onChangeAddress2: TextFieldProps["onChange"];
    onChangeAddress3: TextFieldProps["onChange"];
    onChangeAddress4: TextFieldProps["onChange"];
    onChangeCountry: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    onChangeState: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    onChangeCity: TextFieldProps["onChange"];
    onChangePostalCode: TextFieldProps["onChange"];
    onChangePhone: TextFieldProps["onChange"];
    onChangeEmail: TextFieldProps["onChange"];
    onChangeFax: TextFieldProps["onChange"];
    firstNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    attentionError: React.ReactNode;
    companyNameError: React.ReactNode;
    address1Error: React.ReactNode;
    address2Error: React.ReactNode;
    address3Error: React.ReactNode;
    address4Error: React.ReactNode;
    countryError: React.ReactNode;
    stateError: React.ReactNode;
    cityError: React.ReactNode;
    postalCodeError: React.ReactNode;
    phoneError: React.ReactNode;
    emailError: React.ReactNode;
    faxError: React.ReactNode;
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    validation: CustomerValidationDto;
    extendedStyles?: CustomerAddressFormFieldStyles;
    setShipTo: (_: React.SyntheticEvent<Element, Event>, value: boolean) => void;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CustomerAddressFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    useAsShippingCheckboxGridItem?: GridItemProps;
    useAsShippingCheckbox?: CheckboxPresentationProps;
}

export const customerAddressFormFieldStyles: CustomerAddressFormFieldStyles = {
    useAsShippingCheckboxGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding-top: 48px;

            label {
                color: #2E384D !important;
                font-family: Lato !important;
                font-size: 15px !important;
                letter-spacing: 0.17px;
                line-height: 18px !important;
            }
        `,
    },
    useAsShippingCheckbox: {
        css: css`
        + label {
            color: #2E384D;
            font-family: Lato;
            font-size: 15px;
            letter-spacing: 0.17px;
            line-height: 18px;
        }
        span[role=checkbox] {
            border: 2px solid #424B5A !important;
        }
        
       `,
    },
    container: {
        gap: 10,
        css: css`
            div[class^='FormInputWrapper'], div[class*=' FormInputWrapper']{
                margin-top: 6px !important;
                input:not(.react-datetime-picker__inputGroup__input),
                select:not(.react-datetime-picker__inputGroup__input) {
                    color: #2E384D !important;
                    font-family: 'Lato',sans-serif;
                    font-size: 16px;
                }
                input[data-test-selector="customerAddressForm_firstName"],
                input[data-test-selector="customerAddressForm_lastName"] {
                    font-weight: bold;
                }

                div[class^='DescriptionWrapper'], div[class*='DescriptionWrapper'] {
                    margin-top: 6px;
                }
            }
            label {
                color: #002554;
                font-family: Roboto;
                font-size: 14px;
                font-weight: 500;
                letter-spacing: 0.16px;
                line-height: 23px;

                &[class^='TypographyStyle'] span,
                &[class*=' TypographyStyle'] span { 
                    padding-left: 15px;
                }
            }
            select {
                + span {
                    background: none !important;
                }
            }
        `
    },
    firstNameGridItem: { width: 12 },
    lastNameGridItem: { width: 12 },
    companyNameGridItem: { width: 12 },
    attentionGridItem: { width: 12 },
    address1GridItem: { width: 12 },
    address2GridItem: { width: 12 },
    address3GridItem: { width: 12 },
    address4GridItem: { width: 12 },
    countryGridItem: { width: 12 },
    cityGridItem: { width: 12 },
    stateGridItem: {
        width: 6,
        css: css`
            @media screen and (max-width: 576px) {
                label[class^="TypographyStyle"],label[class*="TypographyStyle"] {
                    span {
                        padding-left: 0 !important;
                        display: block;                       
                    }
                }
            }
        `,
    },
    postalCodeGridItem: {
        width: 6,
        css: css`
            @media screen and (max-width: 576px) {
                label[class^="TypographyStyle"],label[class*="TypographyStyle"] {
                    span {
                        padding-left: 0 !important;
                        display: block;                       
                    }
                }
            }
        `,
    },

    phoneGridItem: { width: 12 },
    faxGridItem: { width: 12 },
    emailGridItem: { width: 12 },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1.875rem;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: .625rem;
        `,
    },
};

const styles = customerAddressFormFieldStyles;

const CustomerAddressFormFields = ({
    firstName,
    lastName,
    attention,
    companyName,
    address1,
    address2,
    address3,
    address4,
    country,
    state,
    city,
    postalCode,
    phone,
    email,
    fax,
    isDefault,
    onChangeFirstName,
    onChangeLastName,
    onChangeAttention,
    onChangeCompanyName,
    onChangeAddress1,
    onChangeAddress2,
    onChangeAddress3,
    onChangeAddress4,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    onChangePostalCode,
    onChangePhone,
    onChangeEmail,
    onChangeFax,
    setShipTo,
    firstNameError,
    lastNameError,
    attentionError,
    companyNameError,
    address1Error,
    address2Error,
    address3Error,
    address4Error,
    countryError,
    stateError,
    cityError,
    postalCodeError,
    phoneError,
    emailError,
    faxError,
    countries,
    fieldDisplay,
    validation,
    extendedStyles,
    currentUserIsGuest,
    isCheckoutShippingPage
}: Props) => {

    const [styles] = React.useState(() => mergeToNew(customerAddressFormFieldStyles, extendedStyles));

    if (!countries) {
        return null;
    }

    const [sortedCountries, setSortedCountries] = useState(countries);
    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);
    const [isCurrentShipTo, setIsCurrentShipTo] = useState(false);

    let featureFlags: string[] = ["TEUS-7942"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enableVerticalAddressLayoutCheckout = flagValues["TEUS-7942"];

    useEffect(() => {
        setIsCurrentShipTo(isDefault);
    }, []);

    const getGridItemWidth = () => {
        return (isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? 12 : 6;
    }

    const dynamicStyles = {
        ...customerAddressFormFieldStyles,
        firstNameGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        lastNameGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        companyNameGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        attentionGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        address1GridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        address2GridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        address3GridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        address4GridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        countryGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        cityGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        phoneGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        faxGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
        emailGridItem: {
            width: getGridItemWidth() as unknown as GridWidths
        },
    };

    useEffect(() => {
        setSelectedCountry(countries.find(c => c.id === country?.id));
    }, [country]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    useEffect(() => {
        if (countries.length === 1 && !selectedCountry) {
            const only = countries[0];
            setSelectedCountry(only);
            // The Select is disabled when there is only one country (e.g. guest checkout, US-only),
            // so onChange never fires and a disabled control's value is never submitted. Propagate the
            // auto-selected country to redux so validation (which reads address.country) agrees with the UI.
            if (country?.id !== only.id) {
                onChangeCountry(
                    { target: { value: only.id } } as unknown as React.ChangeEvent<HTMLSelectElement>,
                    only,
                );
            }
        }
    }, [countries]);

    useEffect(() => {
        const sortCountries = [...countries].sort((a, b) => a.abbreviation.toLowerCase() === "us" || a.abbreviation.toLowerCase() === "mx" || a.abbreviation.toLowerCase() === "ca" ? -1 : 1);
        setSortedCountries(sortCountries);
    }, [])

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        onChangeCountry(event, country || null);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        onChangeState(event, state || null);
    };


    return (
        <GridContainer {...dynamicStyles.container} data-test-selector="customerAddressFormFields">
            {fieldDisplay.firstName!.isVisible && (
                <GridItem {...dynamicStyles.firstNameGridItem}>
                    <TextField
                        {...dynamicStyles.firstNameText}
                        name="firstName"
                        label={fieldDisplay.firstName!.displayName}
                        value={firstName}
                        disabled={false}
                        required={validation.firstName!.isRequired}
                        maxLength={validation.firstName!.maxLength || undefined}
                        onChange={onChangeFirstName}
                        error={firstNameError}
                        data-test-selector="customerAddressForm_firstName"
                    />
                </GridItem>
            )}
            {fieldDisplay.lastName!.isVisible && (
                <GridItem {...dynamicStyles.lastNameGridItem}>
                    <TextField
                        {...dynamicStyles.lastNameText}
                        name="lastName"
                        label={fieldDisplay.lastName!.displayName}
                        value={lastName}
                        disabled={false}
                        required={validation.lastName!.isRequired}
                        maxLength={validation.lastName!.maxLength || undefined}
                        onChange={onChangeLastName}
                        error={lastNameError}
                        data-test-selector="customerAddressForm_lastName"
                    />
                </GridItem>
            )}
            {fieldDisplay.email!.isVisible && (
                <GridItem {...dynamicStyles.emailGridItem}>
                    <TextField
                        {...dynamicStyles.emailText}
                        name="email"
                        label={fieldDisplay.email!.displayName}
                        value={email}
                        disabled={validation.email!.isDisabled}
                        required={validation.email!.isRequired}
                        maxLength={validation.email!.maxLength || undefined}
                        onChange={onChangeEmail}
                        error={emailError}
                        data-test-selector="customerAddressForm_email"
                    />
                </GridItem>
            )}
            {fieldDisplay.phone!.isVisible && (
                <GridItem {...dynamicStyles.phoneGridItem}>
                    <TextField
                        {...dynamicStyles.phoneText}
                        name="phone"
                        label="Phone Number"
                        value={phone}
                        disabled={validation.phone!.isDisabled}
                        required={validation.phone!.isRequired}
                        maxLength={validation.phone!.maxLength || undefined}
                        onChange={onChangePhone}
                        error={phoneError}
                        data-test-selector="customerAddressForm_phone"
                    />
                </GridItem>
            )}
            {fieldDisplay.fax!.isVisible && (
                <GridItem {...dynamicStyles.faxGridItem}>
                    <TextField
                        {...dynamicStyles.faxText}
                        name="fax"
                        label={fieldDisplay.fax!.displayName}
                        value={fax}
                        disabled={validation.fax!.isDisabled}
                        required={validation.fax!.isRequired}
                        maxLength={validation.fax!.maxLength || undefined}
                        onChange={onChangeFax}
                        error={faxError}
                        data-test-selector="customerAddressForm_fax"
                    />
                </GridItem>
            )}
            {fieldDisplay.companyName!.isVisible && (
                <GridItem {...dynamicStyles.companyNameGridItem}>
                    <TextField
                        {...dynamicStyles.companyNameText}
                        name="companyName"
                        label={fieldDisplay.companyName!.displayName}
                        value={companyName}
                        disabled={validation.companyName!.isDisabled}
                        required={validation.companyName!.isRequired}
                        maxLength={validation.companyName!.maxLength || undefined}
                        onChange={onChangeCompanyName}
                        error={companyNameError}
                        data-test-selector="customerAddressForm_companyName"
                    />
                </GridItem>
            )}
            {fieldDisplay.attention!.isVisible && (
                <GridItem {...dynamicStyles.attentionGridItem}>
                    <TextField
                        {...dynamicStyles.attentionText}
                        name="attention"
                        label="Attention to"
                        value={attention}
                        disabled={false}
                        required={validation.attention!.isRequired}
                        maxLength={validation.attention!.maxLength || undefined}
                        onChange={onChangeAttention}
                        error={attentionError}
                        data-test-selector="customerAddressForm_attention"
                    />
                </GridItem>
            )}
            {fieldDisplay.address1!.isVisible && (
                <GridItem {...dynamicStyles.address1GridItem}>
                    <TextField
                        {...dynamicStyles.address1Text}
                        name="address1"
                        label={fieldDisplay.address1!.displayName}
                        value={address1}
                        disabled={validation.address1!.isDisabled}
                        required={validation.address1!.isRequired}
                        maxLength={validation.address1!.maxLength || undefined}
                        onChange={onChangeAddress1}
                        error={address1Error}
                        data-test-selector="customerAddressForm_address1"
                    />
                </GridItem>
            )}
            {fieldDisplay.address2!.isVisible && (
                <GridItem {...dynamicStyles.address2GridItem}>
                    <TextField
                        {...dynamicStyles.address2Text}
                        name="address2"
                        label={fieldDisplay.address2!.displayName}
                        value={address2}
                        disabled={validation.address2!.isDisabled}
                        required={validation.address2!.isRequired}
                        maxLength={validation.address2!.maxLength || undefined}
                        onChange={onChangeAddress2}
                        error={address2Error}
                        data-test-selector="customerAddressForm_address2"
                    />
                </GridItem>
            )}
            {fieldDisplay.country!.isVisible && (
                <GridItem {...dynamicStyles.countryGridItem}>
                    <Select
                        {...dynamicStyles.countrySelect}
                        label={fieldDisplay.country!.displayName}
                        value={selectedCountry?.id || ""}
                        disabled={validation.country!.isDisabled || countries.length === 1}
                        required={validation.country!.isRequired}
                        onChange={handleCountryChange}
                        error={countryError}
                        data-test-selector="customerAddressForm_country"
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.city!.isVisible && (
                <GridItem {...dynamicStyles.cityGridItem}>
                    <TextField
                        {...dynamicStyles.cityText}
                        name="city"
                        label={fieldDisplay.city!.displayName}
                        value={city}
                        disabled={validation.city!.isDisabled}
                        required={validation.city!.isRequired}
                        maxLength={validation.city!.maxLength || undefined}
                        onChange={onChangeCity}
                        error={cityError}
                        data-test-selector="customerAddressForm_city"
                    />
                </GridItem>
            )}
            {states.length > 0 && (
                <GridItem {...dynamicStyles.stateGridItem}>
                    <Select
                        {...dynamicStyles.stateSelect}
                        label={fieldDisplay.state!.displayName}
                        value={state?.id || ""}
                        disabled={validation.state!.isDisabled}
                        required
                        onChange={handleStateChange}
                        error={stateError}
                        data-test-selector="customerAddressForm_state"
                    >
                        <option value="">{translate("Select State")}</option>
                        {states.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.postalCode!.isVisible && (
                <GridItem {...dynamicStyles.postalCodeGridItem}>
                    <TextField
                        {...dynamicStyles.postalCodeText}
                        name="postalCode"
                        label={fieldDisplay.postalCode!.displayName}
                        value={postalCode}
                        disabled={validation.postalCode!.isDisabled}
                        required={validation.postalCode!.isRequired}
                        maxLength={validation.postalCode!.maxLength || undefined}
                        onChange={onChangePostalCode}
                        error={postalCodeError}
                        data-test-selector="customerAddressForm_postalCode"
                    />
                </GridItem>
            )}
            {!isCheckoutShippingPage && (
                <GridItem {...dynamicStyles.useAsShippingCheckboxGridItem}>
                    <Checkbox
                        onChange={setShipTo}
                        checked={isDefault}
                        disabled={isCurrentShipTo}
                        {...dynamicStyles.useAsShippingCheckbox}
                        data-test-selector="address_use_shipping_checkbox"
                    >
                        {translate("Use as shipping address ")}
                    </Checkbox>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(CustomerAddressFormFields);